import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, Building2, Users, Bell, Layers, Sparkles, ArrowRight, MapPin, Eye, UserRound, ChevronRight, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { getGuestFollows } from "@/lib/guest";
import { toHijri } from "@/lib/hijri";

const FEATURES = [
  { icon: CalendarDays, title: "feature_calendar", desc: "feature_calendar_desc", span: "lg:col-span-2" },
  { icon: Building2, title: "feature_mosques", desc: "feature_mosques_desc" },
  { icon: Users, title: "feature_events", desc: "feature_events_desc" },
  { icon: Bell, title: "feature_notifs", desc: "feature_notifs_desc" },
  { icon: Layers, title: "feature_multitenant", desc: "feature_multitenant_desc" },
  { icon: Sparkles, title: "feature_ai", desc: "feature_ai_desc" },
];

export default function Landing() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [assocs, setAssocs] = useState([]);
  const [followedAssocs, setFollowedAssocs] = useState([]);

  useEffect(() => {
    const follows = getGuestFollows();
    if (!follows.length) return;
    Promise.all(follows.map((s) => api.get(`/associations/${s}`).then((r) => r.data).catch(() => null)))
      .then((list) => setFollowedAssocs(list.filter(Boolean)));
  }, []);
  const [showChoice, setShowChoice] = useState(false);
  const today = new Date();
  const hijri = toHijri(today, assocs[0]?.hijri_offset || 0);
  const locale = lang === "fr" ? "fr-FR" : "en-US";

  const continueAsGuest = () => {
    setShowChoice(false);
    if (assocs.length === 1) return navigate(`/${assocs[0].slug}`);
    document.getElementById("assocs")?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    api.get("/associations").then(({ data }) => setAssocs(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <Dialog open={showChoice} onOpenChange={setShowChoice}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("choose_continue")}</DialogTitle>
            <DialogDescription className="sr-only">{t("choose_continue")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button
              data-testid="continue-guest-btn"
              onClick={continueAsGuest}
              className="w-full flex items-center gap-4 border border-border rounded-xl px-5 py-4 text-left hover:bg-muted transition-colors"
            >
              <span className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0"><Eye className="h-5 w-5 text-accent" /></span>
              <span className="min-w-0 flex-1">
                <span className="font-heading font-bold block">{t("continue_guest")}</span>
                <span className="text-xs text-muted-foreground block mt-0.5">{t("guest_desc")}</span>
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
            <button
              data-testid="continue-account-btn"
              onClick={() => navigate("/register")}
              className="w-full flex items-center gap-4 border border-border rounded-xl px-5 py-4 text-left hover:bg-muted transition-colors"
            >
              <span className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0"><UserRound className="h-5 w-5 text-accent" /></span>
              <span className="min-w-0 flex-1">
                <span className="font-heading font-bold block">{t("login_or_register")}</span>
                <span className="text-xs text-muted-foreground block mt-0.5">{t("member_desc")}</span>
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <section className="blueprint-grid relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="text-xs font-mono-data uppercase tracking-[0.2em] text-accent mb-5">{t("tagline")}</p>
            <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl leading-none">
              {t("hero_title")}
            </h1>
            <p className="text-muted-foreground mt-6 max-w-xl leading-relaxed">{t("hero_sub")}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" data-testid="hero-get-started" onClick={() => setShowChoice(true)} className="rounded-full px-7 active:scale-95">
                {t("get_started")} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-explore" onClick={() => document.getElementById("assocs")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full px-7 active:scale-95">
                {t("explore_assocs")}
              </Button>
            </div>
            <div className="mt-10 inline-flex items-center gap-4 border border-border rounded-lg px-5 py-3 bg-card">
              <div>
                <p className="font-heading font-bold capitalize">{today.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
                <p className="text-sm font-mono-data text-muted-foreground">{hijri.label} AH</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="rounded-xl overflow-hidden border border-border shadow-2xl">
              <img src="https://images.unsplash.com/photo-1635016288720-c52507b9a717?w=900&q=80" alt="Mosque" className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {followedAssocs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 -mb-4" data-testid="guest-follows-section">
          <div className="flex items-center gap-2 mb-6">
            <Heart className="h-4 w-4 text-accent" />
            <h2 className="font-heading font-bold text-lg">{t("my_followed_assocs")}</h2>
            <span className="text-[10px] font-mono-data text-muted-foreground border border-border rounded-full px-2 py-0.5">{t("guest_device_note")}</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {followedAssocs.map((a) => (
              <Link key={a.id} to={`/${a.slug}`} data-testid="guest-followed-card"
                className="flex items-center gap-3 border border-border rounded-full pl-1.5 pr-5 py-1.5 bg-card hover-lift">
                <span className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: a.primary_color || "#2563EB" }}>
                  {a.name?.charAt(0)}
                </span>
                <span>
                  <span className="font-heading font-bold text-sm block leading-tight">{a.name}</span>
                  {a.city && <span className="text-[11px] text-muted-foreground">{a.city}</span>}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-heading font-bold text-lg mb-10">{t("features_title")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} className={`border border-border rounded-lg p-6 bg-card hover-lift ${f.span || ""}`} style={{ animationDelay: `${i * 0.05}s` }}>
              <f.icon className="h-5 w-5 text-accent mb-4" />
              <h3 className="font-heading font-bold">{t(f.title)}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t(f.desc)}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="assocs" className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-heading font-bold text-lg mb-10">{t("associations")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="associations-list">
          {assocs.map((a) => (
            <Link key={a.id} to={`/${a.slug}`} data-testid="association-card" className="border border-border rounded-lg overflow-hidden bg-card hover-lift block">
              <div className="h-28 bg-muted relative">
                {a.banner && <img src={a.banner} alt="" className="w-full h-full object-cover" />}
                <span className="absolute bottom-2 left-3 text-[10px] font-mono-data bg-background/80 backdrop-blur px-2 py-0.5 rounded-full border border-border">/{a.slug}</span>
              </div>
              <div className="p-5">
                <h3 className="font-heading font-bold">{a.name}</h3>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  {a.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{a.city}</span>}
                  <span>{a.mosques_count} {t("mosques").toLowerCase()}</span>
                  <span>{a.members_count} {t("members")}</span>
                </div>
              </div>
            </Link>
          ))}
          <Link to="/register-association" data-testid="register-association-cta" className="border border-dashed border-border rounded-lg bg-card/50 hover-lift flex flex-col items-center justify-center text-center p-8 min-h-[180px]">
            <Building2 className="h-6 w-6 text-accent mb-3" />
            <h3 className="font-heading font-bold">{t("register_association")}</h3>
            <p className="text-xs text-muted-foreground mt-1.5">{t("register_association_cta_desc")}</p>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row justify-between gap-4">
          <p className="font-heading font-extrabold">SHIALOC<span className="text-accent">.</span></p>
          <div className="flex items-center gap-5">
            <Link to="/confidentialite" data-testid="footer-privacy-link" className="text-sm text-muted-foreground hover:text-foreground transition-colors">{t("privacy_link")}</Link>
            <p className="text-sm text-muted-foreground">{t("landing_footer")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
